import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ManageBriefTabsService } from '../../../../service/manage-brief-tabs/manage-brief-tabs.service';
import { BriefService } from '@/app/services/brief/brief.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { HireMessageEditor } from '@/app/atoms/hire-message-editor/hire-message-editor';
import {
  applyHireMessageTemplate,
  DEFAULT_HIRE_MESSAGE,
  HIRE_MESSAGE_FIRSTNAME_TOKEN,
} from '@/app/common/hire-message.util';
import { MbaIcon } from '../../../assignment/atoms/mba-icon/mba-icon';
import { bf } from '../../briefing-ui';
import { BriefingDrawer } from '../briefing-drawer/briefing-drawer';
import { BriefingSection } from '../briefing-section/briefing-section';

/** Name used in the "What Alex sees" preview. */
const PREVIEW_NAME = 'Alex';

@Component({
  selector: 'mbt-briefing-hire-message',
  imports: [HireMessageEditor, BriefingSection, BriefingDrawer, MbaIcon],
  templateUrl: './briefing-hire-message.html',
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingHireMessage implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly briefService = inject(BriefService);
  private readonly toastNotificationService = inject(ToastNotificationService);
  readonly manageBriefTabsService = inject(ManageBriefTabsService);

  protected readonly firstNameToken = HIRE_MESSAGE_FIRSTNAME_TOKEN;
  protected readonly bf = bf;
  protected readonly previewName = PREVIEW_NAME;

  readonly flash = input(false);

  protected readonly brandInitial = computed(() => {
    const name = this.manageBriefTabsService.focussedBrief()?.businessAccount?.companyName?.trim();
    return (name?.charAt(0) || 'S').toUpperCase();
  });

  /** The saved message split around {{tokens}} so they render as chips. */
  protected readonly messageParts = computed(() =>
    this.savedMessage()
      .split(/(\{\{\w+\}\})/)
      .filter(Boolean)
      .map((text) => ({ text, token: /^\{\{\w+\}\}$/.test(text) })),
  );

  protected readonly preview = computed(() => applyHireMessageTemplate(this.draft().trim(), PREVIEW_NAME));

  private readonly subscriptionList = new Subscription();

  editing = signal(false);
  saving = signal(false);
  draft = signal('');
  enabledDraft = signal(false);

  private refreshView(): void {
    this.cdr.detectChanges();
  }

  savedMessage(): string {
    return this.manageBriefTabsService.focussedBrief()?.hireMessage?.trim() ?? '';
  }

  isEnabled(): boolean {
    return !!this.manageBriefTabsService.focussedBrief()?.hireMessageEnabled && !!this.savedMessage();
  }

  startEditing(): void {
    const existing = this.savedMessage();
    this.draft.set(existing || DEFAULT_HIRE_MESSAGE);
    this.enabledDraft.set(this.isEnabled());
    this.editing.set(true);
    this.refreshView();
  }

  cancelEditing(): void {
    this.editing.set(false);
    this.saving.set(false);
    this.refreshView();
  }

  save(): void {
    const briefId = this.manageBriefTabsService.focussedBrief()?.id;
    if (!briefId || this.saving()) {
      return;
    }

    const trimmed = this.draft().trim();
    const hireMessage = trimmed.length > 0 ? trimmed : null;
    const hireMessageEnabled = this.enabledDraft() && !!hireMessage;

    this.saving.set(true);
    this.refreshView();

    this.subscriptionList.add(
      this.briefService
        .saveHireMessage(briefId, { hireMessage, hireMessageEnabled })
        .subscribe({
          next: (briefRes) => {
            this.manageBriefTabsService.focussedBrief.set(briefRes);
            this.briefService.focussedBriefSignal.set(briefRes);
            this.briefService.currentBriefSignal.set(briefRes);
            this.saving.set(false);
            this.editing.set(false);
            this.refreshView();
            this.toastNotificationService.alert({
              title: 'Hire message saved',
              message: hireMessageEnabled
                ? 'Creators will receive this chat message when hired.'
                : 'Hire message saved but inactive. Turn Active on to send it on hire.',
              type: 'success',
              icon: 'success',
            });
          },
          error: (err) => {
            console.error('BriefingHireMessage.save ERROR: ', err);
            this.saving.set(false);
            this.refreshView();
            this.toastNotificationService.alert({
              title: 'Error saving hire message',
              message: 'Could not save the hire message. Please try again.',
              type: 'error',
              icon: 'error',
            });
          },
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }
}
